/**
 * Supply-side profile completeness for marketplace advisors (Setup card).
 *
 * Items must be fields the advisor can finish on `/advisors/profile/edit`
 * (plus the account photo). `sessionBookingUrl` is an optional Calendly-style
 * office-hours URL — not collected on Edit, and native session booking already
 * has its own readiness step — so it is not part of this score.
 */

export type AdvisorProfileCompletenessInput = {
  headline?: string | null
  shortBio?: string | null
  bioForAdvising?: string | null
  advisingNote?: string | null
  categoryKeys?: Array<string | null> | null
  preferredCompensationTypeKeys?: Array<string | null> | null
  sessionBookingUrl?: string | null
  availabilityHoursPerMonth?: number | null
  pictureUrl?: string | null
}

export type CompletenessItem = {
  key: string
  label: string
  done: boolean
  hint?: string
}

const nonEmptyKeys = (keys?: Array<string | null> | null) =>
  (keys || []).map(k => (k || '').trim()).filter(Boolean)

export function advisorProfileCompletenessItems(
  profile: AdvisorProfileCompletenessInput | null | undefined,
  opts?: { pictureUrl?: string | null; shortBio?: string | null }
): CompletenessItem[] {
  const pictureUrl = opts?.pictureUrl ?? profile?.pictureUrl
  const shortBio = opts?.shortBio ?? profile?.shortBio
  const headline = profile?.headline?.trim() || shortBio?.trim() || ''
  const advisingBlurb = profile?.bioForAdvising?.trim() || profile?.advisingNote?.trim() || ''
  return [
    {
      key: 'headline',
      label: 'Headline',
      done: headline.length > 0,
      hint: 'One-line bio for the directory',
    },
    {
      key: 'categories',
      label: 'Advice categories',
      done: nonEmptyKeys(profile?.categoryKeys).length > 0,
      hint: 'What founders can hire you for',
    },
    {
      key: 'bio',
      label: 'Advisor bio',
      done: advisingBlurb.length > 20,
      hint: 'Note to founders, or a longer advising bio',
    },
    {
      key: 'comp',
      label: 'Preferred compensation',
      done: nonEmptyKeys(profile?.preferredCompensationTypeKeys).length > 0,
      hint: 'Equity, cash, etc.',
    },
    {
      key: 'photo',
      label: 'Profile photo',
      done: !!(pictureUrl && String(pictureUrl).trim().length > 0),
      hint: 'Account photo shown on your card',
    },
  ]
}

export function advisorProfileCompletenessScore(items: CompletenessItem[]): {
  done: number
  total: number
  percent: number
} {
  const total = items.length
  const done = items.filter(i => i.done).length
  return {
    done,
    total,
    percent: total === 0 ? 0 : Math.round((100 * done) / total),
  }
}
